import { z } from "zod";
import { authenticatedFastApiAxiosInstance as http } from "@/lib/data/http";
export type LibraryScope = "personal" | "firm";
export const libraryItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  isFolder: z.boolean(),
  hasChildren: z.boolean(),
  parentId: z.string().nullable(),
  scope: z.enum(["personal", "firm"]),
  systemFolder: z.string().nullable(),
  revision: z.number(),
  fileContentId: z.string().nullable(),
  contentRevision: z.number().nullable(),
  skillId: z.string().nullable(),
  dateModified: z.string().nullable(),
});
export type LibraryItem = z.infer<typeof libraryItemSchema>;
export async function listLibrary(
  scope: LibraryScope,
  parentId?: string,
  cursor?: string,
) {
  return z
    .object({
      data: z.array(libraryItemSchema),
      nextCursor: z.string().nullable(),
    })
    .parse(
      (
        await http.get("files/library", {
          params: { scope, parentId, cursor, limit: 40 },
        })
      ).data,
    );
}
export async function createLibraryFolder(
  scope: LibraryScope,
  parentId: string | undefined,
  name: string,
) {
  return http.post("files/library/folders", { scope, parentId, name });
}
export async function uploadLibraryFile(
  file: File,
  scope: LibraryScope = "personal",
  parentId?: string,
) {
  const bytes = await file.arrayBuffer();
  const sha256 = Array.from(
    new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)),
    (b) => b.toString(16).padStart(2, "0"),
  ).join("");
  const { data } = await http.post<{
    uploadToken: string;
    url: string;
    headers: Record<string, string>;
  }>("files/library/uploads", {
    scope,
    parentId,
    name: file.name,
    sha256,
    contentType: file.type || "application/octet-stream",
  });
  const put = await fetch(data.url, {
    method: "PUT",
    headers: data.headers,
    body: file,
  });
  if (!put.ok)
    throw new Error("The file could not be uploaded. Please try again.");
  return libraryItemSchema.parse(
    (
      await http.post("files/library/uploads/complete", {
        uploadToken: data.uploadToken,
      })
    ).data,
  );
}
export async function renameLibraryItem(item: LibraryItem, name: string) {
  return http.patch(`files/library/${item.id}`, {
    name,
    revision: item.revision,
  });
}
export async function deleteLibraryItem(item: LibraryItem) {
  return http.delete(`files/library/${item.id}`, {
    params: { revision: item.revision },
  });
}
export type FileMoveSource = Pick<
  LibraryItem,
  "name" | "fileContentId" | "contentRevision"
> & { scope: LibraryScope | "matter" };
export async function moveLibraryItem(
  item: FileMoveSource,
  destination: {
    scope: LibraryScope | "matter";
    parentId?: string;
    matterId?: string;
  },
) {
  return http.post(`file-contents/${item.fileContentId}/move`, {
    ...destination,
    revision: item.contentRevision,
  });
}
export async function downloadLibraryItem(id: string) {
  return (
    await http.get<{ url: string; fileName: string }>(
      `files/library/${id}/download`,
    )
  ).data;
}
export async function getLibraryContent(id: string) {
  return (
    await http.get<{ id: string; revision: string; canonicalContent: string }>(
      `files/library/${id}/content`,
    )
  ).data;
}
export async function saveLibraryContent(
  id: string,
  revision: string,
  canonicalContent: string,
) {
  return http.put(`files/library/${id}/content`, {
    revision,
    canonicalContent,
  });
}
export async function getMatterFileMoveSource(id: string) {
  return (
    await http.get<{ fileContentId: string; contentRevision: number }>(
      `files/library/matter-files/${id}`,
    )
  ).data;
}
